import { useEffect, useState } from "react";
import { Check, Send, MessageSquare } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STAGES = ["submitted", "dbs_checked", "safeguarding_verified", "fa_setup", "approved"];
const LABELS = { submitted: "Submitted", dbs_checked: "DBS checked", safeguarding_verified: "Safeguarding verified", fa_setup: "FA Whole Game set up", approved: "Approved" };
export const COACH_STAGE_LABELS = LABELS;

export function CoachStageStepper({ stage, status }) {
  const idx = status === "approved" ? STAGES.length - 1 : Math.max(0, STAGES.indexOf(stage || "submitted"));
  return (
    <ol className="flex flex-wrap gap-1.5" data-testid="coach-stage-stepper">
      {STAGES.map((s, i) => (
        <li key={s} className={`text-[9px] tracking-widest uppercase px-2 py-1 rounded border inline-flex items-center gap-1 ${i < idx ? "border-[#83B68B]/40 text-[#83B68B] bg-[#83B68B]/10" : i === idx ? (status === "rejected" ? "border-red-400/60 text-red-300" : "border-[#83B68B] text-[#83B68B] bg-[#83B68B]/20 font-bold") : "border-border opacity-40"}`} data-testid={`coach-stage-${s}`}>
          {i < idx && <Check className="w-3 h-3" />}{LABELS[s]}
        </li>
      ))}
    </ol>
  );
}

export function CoachStageSetter({ p, onChanged }) {
  const [v, setV] = useState("");
  const apply = async () => {
    const note = window.prompt("Note for the proposer (optional):") ?? null;
    try { const { data } = await api.post(`/proposals/${p.id}/stage`, { stage: v, note }); onChanged(data); toast.success(`Stage set to ${LABELS[v]} · proposer notified`); setV(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="inline-flex items-center gap-1" data-testid={`coach-set-stage-${p.id}`}>
      <select value={v} onChange={e => setV(e.target.value)} className="bg-transparent border border-border rounded px-2 py-1 text-[10px] tracking-widest uppercase outline-none focus:border-[#83B68B]" data-testid={`coach-set-stage-select-${p.id}`}>
        <option value="">Set stage…</option>
        {STAGES.slice(0, -1).map(s => <option key={s} value={s} disabled={s === (p.stage || "submitted")} className="bg-background">{LABELS[s]}{s === (p.stage || "submitted") ? " (current)" : ""}</option>)}
      </select>
      <button type="button" disabled={!v} onClick={apply} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] disabled:opacity-30" data-testid={`coach-set-stage-apply-${p.id}`}>Apply</button>
    </div>
  );
}

export function ProposalComments({ p, onChanged, compact }) {
  const [open, setOpen] = useState(!compact);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const comments = p.comments || [];
  useEffect(() => { if (comments.length && compact) setOpen(false); }, [p.id]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${p.id}/comments`, { body: text.trim() }); setText(""); onChanged(data); toast.success("Message sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid={`proposal-comments-${p.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid={`proposal-comments-toggle-${p.id}`}>
        <MessageSquare className="w-3 h-3" /> Messages{comments.length ? ` (${comments.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 rounded border border-border p-3 space-y-2">
          {comments.length === 0 && <div className="text-xs opacity-50">No messages yet.</div>}
          {comments.map(c => (
            <div key={c.id} className={`text-xs rounded px-3 py-2 ${c.is_admin ? "bg-[#83B68B]/10 border border-[#83B68B]/30" : "bg-muted"}`} data-testid={`proposal-comment-${c.id}`}>
              <div className="opacity-60 text-[10px]">{c.by_name}{c.is_admin ? " · Admin" : ""} · {format(parseISO(c.at), "d MMM HH:mm")}</div>
              <div className="mt-0.5 whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
          {p.status === "pending" && (
            <div className="flex items-end gap-2 pt-1">
              <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Add information or ask a question…" className="flex-1 bg-transparent border border-border rounded px-2 py-1.5 text-xs outline-none focus:border-[#83B68B]" data-testid={`proposal-comment-input-${p.id}`} />
              <button type="button" disabled={busy || !text.trim()} onClick={send} className="btn-volt p-2 rounded disabled:opacity-40" data-testid={`proposal-comment-send-${p.id}`}><Send className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ProposalHistory({ p }) {
  const h = p.history || [];
  if (!h.length) return null;
  return (
    <ul className="mt-2 text-[11px] opacity-70 space-y-0.5" data-testid={`proposal-history-${p.id}`}>
      {[...h].reverse().map((x, i) => <li key={`${x.at}-${i}`}><span className="opacity-60">{format(parseISO(x.at), "d MMM HH:mm")}</span> · {LABELS[x.stage] || x.stage} · {x.by_name}{x.note ? ` — ${x.note}` : ""}</li>)}
    </ul>
  );
}
